import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  /** Custom fallback UI. Receives the caught error. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type State = {
  error: Error | null;
};

/**
 * Class-based error boundary.
 *
 * Catches rendering errors in the subtree and renders either a custom
 * `fallback` or a default recovery UI, keeping the outer shell (nav,
 * sidebar) intact.
 *
 * @example
 * <ErrorBoundary>
 *   <Outlet />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production wire this to your error-reporting service.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (fallback) return fallback(error, this.reset);
      return <DefaultErrorFallback error={error} reset={this.reset} />;
    }

    return children;
  }
}

// ---------- Default fallback ----------

type FallbackProps = { error: Error; reset: () => void };

// eslint-disable-next-line react-refresh/only-export-components
const DefaultErrorFallback = ({ error, reset }: FallbackProps) => (
  <div
    role="alert"
    style={{
      padding: '40px 20px',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
    }}
  >
    <h2 style={{ color: '#c00', fontSize: '20px' }}>Щось пішло не так</h2>
    <p style={{ color: '#666', fontSize: '14px', maxWidth: '480px' }}>
      {error.message || 'Невідома помилка. Спробуйте оновити сторінку.'}
    </p>
    <button
      onClick={reset}
      style={{
        background: '#004795',
        color: '#fff',
        border: 'none',
        borderRadius: '50px',
        padding: '10px 20px',
        fontSize: '14px',
        cursor: 'pointer',
      }}
    >
      Спробувати знову
    </button>
  </div>
);
